/**
 * Debug Menu - In-game debug panel with toggleable options
 * 
 * Features:
 * - Collapsible debug panel
 * - Toggle switches for debug categories
 * - Real-time stats display
 * - Quick actions (teleport, spawn, etc.)
 * - Physics controls
 * - Camera controls
 */

import { IDisposable, IUpdatable } from "../core/Engine.types";
import { DebugVisualizer, DebugCategory } from "./DebugVisualizer";
import { Scene } from "@babylonjs/core/scene";
import type { Camera, CameraMode } from "../camera/Camera";

/**
 * Debug menu configuration
 */
export interface DebugMenuConfig {
    /** Initial visibility */
    visible: boolean;
    /** Position on screen */
    position: "top-left" | "top-right" | "bottom-left" | "bottom-right";
    /** Hotkey to toggle menu (default: F3) */
    toggleKey: string;
    /** Theme */
    theme: "dark" | "light";
    /** Show performance stats */
    showStats: boolean;
    /** Enable keyboard shortcuts */
    enableShortcuts: boolean;
}

/**
 * Debug action callback
 */
export type DebugAction = () => void;

/**
 * Registered debug action
 */
interface DebugActionInfo {
    label: string;
    shortcut?: string;
    callback: DebugAction;
    category: string;
}

/**
 * Default debug menu configuration
 */
const DEFAULT_CONFIG: DebugMenuConfig = {
    visible: false,
    position: "top-right",
    toggleKey: "F3",
    theme: "dark",
    showStats: true,
    enableShortcuts: true,
};

/**
 * DebugMenu - In-game debug control panel
 */
export class DebugMenu implements IDisposable, IUpdatable {
    // Scene reference kept for future features like picking/raycasting
    private _scene: Scene;
    private _config: DebugMenuConfig;
    private _visualizer: DebugVisualizer;
    private _container: HTMLElement | null = null;
    private _statsContainer: HTMLElement | null = null;
    private _actionsContainer: HTMLElement | null = null;
    private _visible: boolean = false;
    private _collapsed: boolean = false;
    private _actions: Map<string, DebugActionInfo> = new Map();

    /**
     * Get the scene this menu is attached to
     */
    public get scene(): Scene {
        return this._scene;
    }
    private _toggles: Map<string, { element: HTMLElement; value: boolean }> = new Map();
    private _keydownHandler: ((e: KeyboardEvent) => void) | null = null;
    private _camera: Camera | null = null;
    private _cameraSection: HTMLElement | null = null;
    
    // Callbacks
    private _onToggleCallback?: (category: string, enabled: boolean) => void;
    private _onActionCallback?: (actionId: string) => void;

    public get visible(): boolean {
        return this._visible;
    }

    public get visualizer(): DebugVisualizer {
        return this._visualizer;
    }

    constructor(scene: Scene, config?: Partial<DebugMenuConfig>) {
        this._scene = scene;
        this._config = { ...DEFAULT_CONFIG, ...config };
        
        // Create the debug visualizer
        this._visualizer = new DebugVisualizer(scene, {
            showBoundingBoxes: true,
            showCollisionShapes: true,
            showMapAssetBounds: true,
        });
        
        // Create UI
        this._createUI();
        
        // Setup keyboard shortcuts
        if (this._config.enableShortcuts) {
            this._setupKeyboardShortcuts();
        }
        
        // Set initial visibility
        if (this._config.visible) {
            this.show();
        }
    }

    /**
     * Create the debug menu UI
     */
    private _createUI(): void {
        // Main container
        this._container = document.createElement("div");
        this._container.id = "debug-menu";
        this._container.style.cssText = this._getContainerStyles();
        
        // Header
        const header = this._createHeader();
        this._container.appendChild(header);
        
        // Content wrapper
        const content = document.createElement("div");
        content.id = "debug-menu-content";
        content.style.cssText = "max-height: 500px; overflow-y: auto;";
        
        // Stats section
        if (this._config.showStats) {
            this._statsContainer = this._createStatsSection();
            content.appendChild(this._statsContainer);
        }
        
        // Debug toggles section
        const togglesSection = this._createTogglesSection();
        content.appendChild(togglesSection);
        
        // Actions section
        this._actionsContainer = this._createActionsSection();
        content.appendChild(this._actionsContainer);
        
        this._container.appendChild(content);
        
        // Add to document
        document.body.appendChild(this._container);
        
        // Initially hidden
        this._container.style.display = "none";
    }

    /**
     * Get container styles based on position
     */
    private _getContainerStyles(): string {
        const isDark = this._config.theme === "dark";
        const base = `
            position: fixed;
            z-index: 10000;
            font-family: 'Consolas', 'Monaco', monospace;
            font-size: 12px;
            background: ${isDark ? "rgba(20, 20, 30, 0.95)" : "rgba(255, 255, 255, 0.95)"};
            color: ${isDark ? "#e0e0e0" : "#333"};
            border: 1px solid ${isDark ? "#444" : "#ccc"};
            border-radius: 8px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
            min-width: 280px;
            max-width: 350px;
        `;
        
        const positions: Record<string, string> = {
            "top-left": "top: 10px; left: 10px;",
            "top-right": "top: 10px; right: 10px;",
            "bottom-left": "bottom: 10px; left: 10px;",
            "bottom-right": "bottom: 10px; right: 10px;",
        };
        
        return base + positions[this._config.position];
    }

    /**
     * Create header with title and collapse button
     */
    private _createHeader(): HTMLElement {
        const header = document.createElement("div");
        header.style.cssText = `
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 12px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            cursor: pointer;
            user-select: none;
        `;
        
        const title = document.createElement("span");
        title.textContent = "🔧 Debug Menu";
        title.style.fontWeight = "bold";
        
        const collapseBtn = document.createElement("span");
        collapseBtn.textContent = "▼";
        collapseBtn.style.transition = "transform 0.2s";
        
        header.appendChild(title);
        header.appendChild(collapseBtn);
        
        header.addEventListener("click", () => {
            this._collapsed = !this._collapsed;
            const content = this._container?.querySelector("#debug-menu-content") as HTMLElement;
            if (content) {
                content.style.display = this._collapsed ? "none" : "block";
                collapseBtn.style.transform = this._collapsed ? "rotate(-90deg)" : "rotate(0)";
            }
        });
        
        return header;
    }

    /**
     * Create stats display section
     */
    private _createStatsSection(): HTMLElement {
        const section = document.createElement("div");
        section.style.cssText = `
            padding: 10px 12px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            font-family: monospace;
        `;
        
        const title = document.createElement("div");
        title.textContent = "📊 Performance";
        title.style.cssText = "font-weight: bold; margin-bottom: 8px;";
        section.appendChild(title);
        
        const stats = document.createElement("div");
        stats.id = "debug-stats";
        stats.style.cssText = `
            display: grid;
            grid-template-columns: auto auto;
            gap: 4px 16px;
            font-size: 11px;
        `;
        stats.innerHTML = `
            <span>FPS:</span><span id="stat-fps">--</span>
            <span>Frame:</span><span id="stat-frame">--ms</span>
            <span>Draw Calls:</span><span id="stat-drawcalls">--</span>
            <span>Entities:</span><span id="stat-entities">--</span>
            <span>Collisions:</span><span id="stat-collisions">--</span>
        `;
        section.appendChild(stats);
        
        return section;
    }

    /**
     * Create debug toggles section
     */
    private _createTogglesSection(): HTMLElement {
        const section = document.createElement("div");
        section.style.cssText = `
            padding: 10px 12px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        `;
        
        const title = document.createElement("div");
        title.textContent = "🎯 Debug Visualizations";
        title.style.cssText = "font-weight: bold; margin-bottom: 8px;";
        section.appendChild(title);
        
        // Create toggles for each debug category
        // Note: defaults should match the actual system states (all start disabled)
        const toggles = [
            { id: "debug-enabled", label: "Enable Debug", default: false, global: true },
            { id: "bounding-boxes", label: "Bounding Boxes", default: false, category: DebugCategory.ENTITIES },
            { id: "collision-shapes", label: "Collision Shapes", default: false, category: DebugCategory.MAP_ASSETS },
            { id: "terrain-grid", label: "Terrain Grid", default: false, category: DebugCategory.TERRAIN },
            { id: "physics-bodies", label: "Physics Bodies", default: false, category: DebugCategory.PHYSICS },
            { id: "spawn-points", label: "Spawn Points", default: false, category: DebugCategory.SPAWN_POINTS },
        ];
        
        for (const toggle of toggles) {
            const row = this._createToggleRow(toggle.id, toggle.label, toggle.default, (enabled) => {
                if (toggle.global) {
                    this._visualizer.setEnabled(enabled);
                } else if (toggle.category) {
                    this._visualizer.setCategoryVisible(toggle.category, enabled);
                }
                this._onToggleCallback?.(toggle.id, enabled);
            });
            section.appendChild(row);
        }
        
        return section;
    }

    /**
     * Create a toggle row
     */
    private _createToggleRow(
        id: string,
        label: string,
        defaultValue: boolean,
        onChange: (enabled: boolean) => void
    ): HTMLElement {
        const row = document.createElement("div");
        row.style.cssText = `
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 4px 0;
        `;
        
        const labelEl = document.createElement("span");
        labelEl.textContent = label;
        
        const toggle = document.createElement("div");
        toggle.className = "debug-toggle";
        toggle.style.cssText = `
            width: 40px;
            height: 20px;
            background: ${defaultValue ? "#4CAF50" : "#666"};
            border-radius: 10px;
            position: relative;
            cursor: pointer;
            transition: background 0.2s;
        `;
        
        const knob = document.createElement("div");
        knob.style.cssText = `
            width: 16px;
            height: 16px;
            background: white;
            border-radius: 50%;
            position: absolute;
            top: 2px;
            left: ${defaultValue ? "22px" : "2px"};
            transition: left 0.2s;
        `;
        toggle.appendChild(knob);
        
        let value = defaultValue;
        this._toggles.set(id, { element: toggle, value });
        
        toggle.addEventListener("click", () => {
            value = !value;
            toggle.style.background = value ? "#4CAF50" : "#666";
            knob.style.left = value ? "22px" : "2px";
            this._toggles.get(id)!.value = value;
            onChange(value);
        });
        
        row.appendChild(labelEl);
        row.appendChild(toggle);
        
        return row;
    }

    /**
     * Create actions section
     */
    private _createActionsSection(): HTMLElement {
        const section = document.createElement("div");
        section.style.cssText = "padding: 10px 12px;";
        
        const title = document.createElement("div");
        title.textContent = "⚡ Quick Actions";
        title.style.cssText = "font-weight: bold; margin-bottom: 8px;";
        section.appendChild(title);
        
        const actionsGrid = document.createElement("div");
        actionsGrid.id = "debug-actions";
        actionsGrid.style.cssText = `
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 6px;
        `;
        section.appendChild(actionsGrid);
        
        return section;
    }

    /**
     * Create camera controls section
     */
    private _createCameraSection(): HTMLElement {
        const section = document.createElement("div");
        section.style.cssText = `
            padding: 10px 12px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        `;
        
        const title = document.createElement("div");
        title.textContent = "📷 Camera Controls";
        title.style.cssText = "font-weight: bold; margin-bottom: 8px;";
        section.appendChild(title);
        
        // Camera mode selector
        const modeRow = document.createElement("div");
        modeRow.style.cssText = "margin-bottom: 8px;";
        
        const modeLabel = document.createElement("div");
        modeLabel.textContent = "Mode:";
        modeLabel.style.cssText = "margin-bottom: 4px; font-size: 11px; color: #aaa;";
        modeRow.appendChild(modeLabel);
        
        const modeButtons = document.createElement("div");
        modeButtons.id = "camera-mode-buttons";
        modeButtons.style.cssText = `
            display: flex;
            gap: 4px;
            flex-wrap: wrap;
        `;
        
        const modes: { mode: CameraMode; label: string; shortcut: string }[] = [
            { mode: "free", label: "Free", shortcut: "1" },
            { mode: "follow", label: "Follow", shortcut: "2" },
            { mode: "fps", label: "FPS", shortcut: "3" },
            { mode: "orbit", label: "Orbit", shortcut: "4" },
        ];
        
        for (const { mode, label, shortcut } of modes) {
            const btn = document.createElement("button");
            btn.id = `camera-mode-${mode}`;
            btn.textContent = `${label} [${shortcut}]`;
            btn.style.cssText = `
                padding: 4px 8px;
                background: #3a3a4a;
                border: 1px solid #555;
                border-radius: 4px;
                color: #e0e0e0;
                cursor: pointer;
                font-size: 10px;
                transition: background 0.2s;
            `;
            btn.addEventListener("click", () => this._setCameraMode(mode));
            btn.addEventListener("mouseenter", () => {
                btn.style.background = "#4a4a5a";
            });
            btn.addEventListener("mouseleave", () => {
                if (this._camera?.currentMode !== mode) {
                    btn.style.background = "#3a3a4a";
                }
            });
            modeButtons.appendChild(btn);
        }
        
        modeRow.appendChild(modeButtons);
        section.appendChild(modeRow);
        
        // Current camera info
        const infoRow = document.createElement("div");
        infoRow.id = "camera-info";
        infoRow.style.cssText = `
            font-size: 10px;
            color: #888;
            padding-top: 4px;
            border-top: 1px solid rgba(255, 255, 255, 0.05);
        `;
        infoRow.innerHTML = `
            <div>Current: <span id="camera-current-mode">--</span></div>
            <div>Target: <span id="camera-follow-target">--</span></div>
        `;
        section.appendChild(infoRow);
        
        return section;
    }

    /**
     * Set camera mode
     */
    private _setCameraMode(mode: CameraMode): void {
        if (this._camera) {
            this._camera.setMode(mode);
            this._updateCameraModeButtons(mode);
        }
    }

    /**
     * Update camera mode button states
     */
    private _updateCameraModeButtons(activeMode: CameraMode): void {
        const modes = ["free", "follow", "fps", "orbit"];
        for (const mode of modes) {
            const btn = document.getElementById(`camera-mode-${mode}`);
            if (btn) {
                const isActive = mode === activeMode;
                btn.style.background = isActive ? "#4CAF50" : "#3a3a4a";
                btn.style.borderColor = isActive ? "#6FD16F" : "#555";
            }
        }
        
        // Update info display
        const currentEl = document.getElementById("camera-current-mode");
        if (currentEl) {
            currentEl.textContent = activeMode.charAt(0).toUpperCase() + activeMode.slice(1);
        }
    }

    /**
     * Set the unified camera for camera controls
     */
    public setCamera(camera: Camera): void {
        this._camera = camera;
        
        // Add camera section if not already added
        if (!this._cameraSection && this._container) {
            this._cameraSection = this._createCameraSection();
            const content = this._container.querySelector("#debug-menu-content");
            if (content && this._actionsContainer) {
                // Insert before actions section
                content.insertBefore(this._cameraSection, this._actionsContainer);
            }
        }
        
        // Setup mode change listener
        camera.on("modeChanged", (event) => {
            this._updateCameraModeButtons(event.current);
        });
        
        // Initialize button states
        this._updateCameraModeButtons(camera.currentMode);
    }

    /**
     * Update camera follow target display
     */
    public updateCameraTargetDisplay(targetName: string): void {
        const targetEl = document.getElementById("camera-follow-target");
        if (targetEl) {
            targetEl.textContent = targetName || "None";
        }
    }

    /**
     * Register a debug action button
     */
    public registerAction(
        id: string,
        label: string,
        callback: DebugAction,
        category: string = "general",
        shortcut?: string
    ): void {
        this._actions.set(id, { label, callback, category, shortcut });
        this._addActionButton(id, label, shortcut);
    }

    /**
     * Add an action button to the UI
     */
    private _addActionButton(id: string, label: string, shortcut?: string): void {
        const actionsGrid = this._actionsContainer?.querySelector("#debug-actions");
        if (!actionsGrid) return;
        
        const btn = document.createElement("button");
        btn.id = `debug-action-${id}`;
        btn.style.cssText = `
            padding: 6px 8px;
            background: #3a3a4a;
            border: 1px solid #555;
            border-radius: 4px;
            color: #e0e0e0;
            cursor: pointer;
            font-size: 11px;
            transition: background 0.2s;
        `;
        btn.textContent = shortcut ? `${label} [${shortcut}]` : label;
        
        btn.addEventListener("mouseenter", () => {
            btn.style.background = "#4a4a5a";
        });
        btn.addEventListener("mouseleave", () => {
            btn.style.background = "#3a3a4a";
        });
        btn.addEventListener("click", () => {
            this._executeAction(id);
        });
        
        actionsGrid.appendChild(btn);
    }

    /**
     * Execute a registered action
     */
    private _executeAction(id: string): void {
        const action = this._actions.get(id);
        if (action) {
            action.callback();
            this._onActionCallback?.(id);
        }
    }

    /**
     * Setup keyboard shortcuts
     */
    private _setupKeyboardShortcuts(): void {
        this._keydownHandler = (e: KeyboardEvent) => {
            // Toggle menu
            if (e.key === this._config.toggleKey) {
                e.preventDefault();
                this.toggle();
                return;
            }
            
            // Camera mode shortcuts (when debug menu is visible)
            if (this._visible && this._camera) {
                const cameraModeKeys: Record<string, CameraMode> = {
                    "1": "free",
                    "2": "follow",
                    "3": "fps",
                    "4": "orbit",
                };
                if (cameraModeKeys[e.key]) {
                    e.preventDefault();
                    this._setCameraMode(cameraModeKeys[e.key]);
                    return;
                }
            }
            
            // Check action shortcuts (case-insensitive)
            if (this._visible) {
                for (const [id, action] of this._actions) {
                    if (action.shortcut && e.key.toLowerCase() === action.shortcut.toLowerCase()) {
                        e.preventDefault();
                        this._executeAction(id);
                        return;
                    }
                }
            }
        };
        
        document.addEventListener("keydown", this._keydownHandler);
    }

    /**
     * Show the debug menu
     */
    public show(): void {
        this._visible = true;
        if (this._container) {
            this._container.style.display = "block";
        }
    }

    /**
     * Hide the debug menu
     */
    public hide(): void {
        this._visible = false;
        if (this._container) {
            this._container.style.display = "none";
        }
    }

    /**
     * Toggle menu visibility
     */
    public toggle(): void {
        if (this._visible) {
            this.hide();
        } else {
            this.show();
        }
    }

    /**
     * Set toggle callback
     */
    public onToggle(callback: (category: string, enabled: boolean) => void): void {
        this._onToggleCallback = callback;
    }

    /**
     * Set action callback
     */
    public onAction(callback: (actionId: string) => void): void {
        this._onActionCallback = callback;
    }

    /**
     * Update stats display
     */
    public update(_deltaTime: number): void {
        if (!this._visible || !this._config.showStats) return;
        
        // Update visualizer stats
        this._visualizer.update(_deltaTime);
        
        // Update stats display
        const stats = this._visualizer.stats;
        this._updateStatElement("stat-fps", stats.fps.toString());
        this._updateStatElement("stat-frame", `${stats.frameTime.toFixed(1)}ms`);
        this._updateStatElement("stat-drawcalls", stats.drawCalls.toString());
        this._updateStatElement("stat-entities", stats.entityCount.toString());
        this._updateStatElement("stat-collisions", stats.collisionCount.toString());
    }

    /**
     * Update a stat element
     */
    private _updateStatElement(id: string, value: string): void {
        const el = document.getElementById(id);
        if (el) {
            el.textContent = value;
        }
    }

    /**
     * Update entity count stat
     */
    public setEntityCount(count: number): void {
        this._visualizer.updateStats({ entityCount: count });
    }

    /**
     * Update collision count stat
     */
    public setCollisionCount(count: number): void {
        this._visualizer.updateStats({ collisionCount: count });
    }

    /**
     * Set a toggle value programmatically
     */
    public setToggle(id: string, value: boolean): void {
        const toggle = this._toggles.get(id);
        if (toggle && toggle.value !== value) {
            toggle.element.click();
        }
    }

    /**
     * Get toggle value
     */
    public getToggle(id: string): boolean {
        return this._toggles.get(id)?.value ?? false;
    }

    /**
     * Dispose resources
     */
    public dispose(): void {
        if (this._keydownHandler) {
            document.removeEventListener("keydown", this._keydownHandler);
        }
        
        if (this._container && this._container.parentNode) {
            this._container.parentNode.removeChild(this._container);
        }
        
        this._visualizer.dispose();
        this._actions.clear();
        this._toggles.clear();
    }
}

export { DebugCategory };
